import { assertNever, panic } from "@glideapps/ts-necessities";
import type { AppAuthentication, AppDescription, EmailWithPin } from "./components/SerializedApp";
import {
    AppAuthenticationKind,
    AuthenticationEmailSource,
    getAuthenticationEmailSource,
} from "./components/SerializedApp";
import { getUserProfileTableInfo } from "./components/user-profile";
import { AuthenticationMethod } from "./Database";
import type { TableName } from "./database-strings";
import type { AuthenticationSupport } from "./Database/eminence";
import { makeTableName } from "./generator/description";
import { makeRandomPassword } from "./Support";

interface PrimitiveAuthenticationData {
    readonly kind: AuthenticationMethod.None | AuthenticationMethod.Disabled;
}

interface EmailPinAuthenticationData {
    readonly kind: AuthenticationMethod.PublicEmailPin | AuthenticationMethod.OrgMembers | AuthenticationMethod.JustMe;
    readonly optional: boolean;
}

interface PasswordAuthenticationData {
    readonly kind: AuthenticationMethod.Password;
    readonly password: string;
}

interface WhitelistEmailPinAuthenticationData {
    readonly kind: AuthenticationMethod.WhitelistEmailPin;
    readonly emailTableName: TableName;
    readonly additionalEmailsWithPins: readonly EmailWithPin[];
}

interface UserProfileEmailPinAuthenticationData {
    readonly kind: AuthenticationMethod.UserProfileEmailPin;
    readonly tableName?: TableName;
    readonly columnName?: string;
    readonly optional: boolean;
}

export type AuthenticationData =
    | PrimitiveAuthenticationData
    | EmailPinAuthenticationData
    | PasswordAuthenticationData
    | WhitelistEmailPinAuthenticationData
    | UserProfileEmailPinAuthenticationData;

export function isAuthenticationRequired(auth: AuthenticationData): boolean {
    switch (auth.kind) {
        case AuthenticationMethod.None:
        case AuthenticationMethod.Disabled:
            return false;
        case AuthenticationMethod.PublicEmailPin:
        case AuthenticationMethod.OrgMembers:
        case AuthenticationMethod.JustMe:
        case AuthenticationMethod.UserProfileEmailPin:
            return !auth.optional;
        case AuthenticationMethod.WhitelistEmailPin:
        case AuthenticationMethod.Password:
            return true;
        default:
            return assertNever(auth);
    }
}

export function getAppAuthenticationData(app: AppDescription): AuthenticationData {
    const { authentication } = app;
    const userProfile = getUserProfileTableInfo(app);

    if (authentication === undefined) {
        // `undefined` always means no authentication required.
        return { kind: AuthenticationMethod.PublicEmailPin, optional: true };
    }

    if (authentication.kind === AppAuthenticationKind.Disabled) {
        return { kind: AuthenticationMethod.Disabled };
    }

    if (authentication.kind === undefined || authentication.kind === AppAuthenticationKind.Password) {
        return { kind: AuthenticationMethod.Password, password: authentication.password };
    }

    if (authentication.kind === AppAuthenticationKind.EmailPin) {
        const source = getAuthenticationEmailSource(authentication);
        const optional = authentication.optional ?? false;
        if (source === AuthenticationEmailSource.Table) {
            const { emailTableName, additionalEmailsWithPins } = authentication;
            if (emailTableName === undefined) {
                return { kind: AuthenticationMethod.PublicEmailPin, optional };
            } else {
                return {
                    kind: AuthenticationMethod.WhitelistEmailPin,
                    emailTableName: makeTableName(emailTableName),
                    additionalEmailsWithPins: additionalEmailsWithPins ?? [],
                };
            }
        } else if (source === AuthenticationEmailSource.OrgMembers) {
            return { kind: AuthenticationMethod.OrgMembers, optional };
        } else if (source === AuthenticationEmailSource.JustMe) {
            return { kind: AuthenticationMethod.JustMe, optional };
        } else if (source === AuthenticationEmailSource.UserProfiles) {
            return {
                kind: AuthenticationMethod.UserProfileEmailPin,
                tableName: userProfile?.tableName,
                columnName: userProfile?.emailColumnName,
                optional,
            };
        } else {
            assertNever(source);
        }
    }

    assertNever(authentication.kind);
}

export function enforceEminenceInAuthentication(
    app: AppDescription,
    support: AuthenticationSupport
): readonly [auth: AppAuthentication | undefined, didChange: boolean] {
    const info = getAppAuthenticationData(app);

    let mustChange = false;

    switch (info.kind) {
        case AuthenticationMethod.None:
            mustChange = true;
            break;
        case AuthenticationMethod.Disabled:
            mustChange = support.publicDisabled !== true;
            break;
        case AuthenticationMethod.PublicEmailPin:
            mustChange =
                support.publicWithEmail !== true || (info.optional && support.optionalPublicWithEmail !== true);
            break;
        case AuthenticationMethod.OrgMembers:
            mustChange = support.organizationMembers !== true || (info.optional && support.optionalOtherEmail !== true);
            break;
        case AuthenticationMethod.JustMe:
            mustChange = support.justMe !== true || (info.optional && support.optionalOtherEmail !== true);
            break;
        case AuthenticationMethod.Password:
            mustChange = support.password !== true;
            break;
        case AuthenticationMethod.WhitelistEmailPin:
            mustChange = support.emailWhitelist !== true;
            break;
        case AuthenticationMethod.UserProfileEmailPin:
            mustChange = support.userProfiles !== true || (info.optional && support.optionalOtherEmail !== true);
            break;
        default:
            return assertNever(info);
    }

    if (!mustChange) {
        return [app.authentication, false];
    }

    const { authentication } = app;
    let newAuth: AppAuthentication | undefined;
    if (support.organizationMembers === true) {
        newAuth = {
            ...authentication,
            kind: AppAuthenticationKind.EmailPin,
            source: AuthenticationEmailSource.OrgMembers,
            emailTableName: undefined,
            optional: false,
        };
    } else if (support.publicWithEmail === true) {
        newAuth = {
            ...authentication,
            kind: AppAuthenticationKind.EmailPin,
            source: AuthenticationEmailSource.Table,
            emailTableName: undefined,
            optional: false,
        };
    } else if (support.justMe === true) {
        newAuth = {
            ...authentication,
            kind: AppAuthenticationKind.EmailPin,
            source: AuthenticationEmailSource.JustMe,
            emailTableName: undefined,
            optional: false,
        };
    } else if (support.password === true) {
        newAuth = {
            ...authentication,
            kind: AppAuthenticationKind.Password,
            password: makeRandomPassword(),
        };
    } else {
        return panic("Eminence doesn't support any suitable authentication method");
    }

    return [newAuth, true];
}

export function doesAuthenticationMethodAllowSignUp(method: AuthenticationMethod): boolean {
    switch (method) {
        case AuthenticationMethod.None:
        case AuthenticationMethod.PublicEmailPin:
        case AuthenticationMethod.WhitelistEmailPin:
        case AuthenticationMethod.UserProfileEmailPin:
            return true;

        case AuthenticationMethod.JustMe:
        case AuthenticationMethod.Disabled:
        case AuthenticationMethod.Password:
        case AuthenticationMethod.OrgMembers:
            return false;

        default:
            return assertNever(method);
    }
}
